/**
 * Activation Analytics
 *
 * Client-safe helpers for activation funnel tracking.
 *
 * Primary responsibilities:
 * - Track typed activation events through the shared analytics route
 * - Fill stable anonymous session ids for chat lifecycle events
 * - Derive privacy-safe labels and public-session activation state
 *
 * Dependencies:
 * - lib/analytics/track
 * - lib/analytics/activation-event-contract
 */

import { EventNames, getOrCreateSessionId, track } from './track'
import {
  ActivationChatType,
  ActivationErrorType,
  ActivationEventName,
  ActivationEventNames,
  ActivationEventPropertiesByName,
  ActivationFailureStage,
  ActivationReferrerType,
  ActivationSurface,
  ChatFirstAiResponseFailedProperties,
  ChatFirstAiResponseStartedProperties,
  ChatFirstAiResponseSucceededProperties,
  ChatFirstUserMessageSubmittedProperties,
  ChatSecondUserMessageSubmittedProperties,
  PublicChatStarterPromptClickedProperties,
  SharedChatFollowupSubmittedProperties,
  buildActivationEvent
} from './activation-event-contract'

type WithOptionalSessionId<T extends { session_id: string }> =
  Omit<T, 'session_id'> & {
    session_id?: string | null
  }

export interface PublicSessionActivationState {
  had_first_ai_response: boolean
  had_second_message: boolean
}

export interface FirstChatMessageSentProperties {
  message_length: number
  is_first_message: boolean
  surface: 'chat'
}

export interface ChatMessageSentProperties {
  message_length: number
  is_first_message: boolean
  surface:
    | 'homepage'
    | 'chat'
    | 'public_chat'
    | 'spaces'
    | 'sidekick'
    | 'embed'
    | 'nexus'
    | 'unknown'
}

export interface HomepageChatSubmittedProperties {
  message_length: number
  entry_point:
    | 'homepage_hero'
    | 'homepage_section'
    | 'homepage_starter'
    | 'homepage_trending'
    | 'homepage_legacy'
  destination: '/try' | 'try'
  has_attachments: boolean
  is_anonymous: boolean
}

export interface HomepageChatResponseShownProperties {
  is_anonymous: boolean
  message_count: number
}

export interface HomepageCtaClickedProperties {
  target: 'signup'
  is_anonymous: boolean
  message_count: number
}

function withSessionId<T extends { session_id: string }>(
  properties: WithOptionalSessionId<T>
): T {
  return {
    ...properties,
    session_id: properties.session_id?.trim() || getOrCreateSessionId()
  } as T
}

function getRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  return value as Record<string, unknown>
}

function readMessageRole(value: unknown) {
  const record = getRecord(value)
  const message = getRecord(record?.message)
  const role = message?.role

  return typeof role === 'string' ? role : null
}

function fallbackHash(value: string) {
  let hash = 0x811c9dc5

  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index)
    hash = Math.imul(hash, 0x01000193)
  }

  return `hash_${(hash >>> 0).toString(16).padStart(8, '0')}`
}

export function getActivationSessionId() {
  return getOrCreateSessionId()
}

export function getActivationSurfaceFromPath(
  pathname: string | null | undefined,
  isAnonymous: boolean
): ActivationSurface {
  const path = pathname || ''

  if (path.startsWith('/try')) return 'public_chat'
  if (path.startsWith('/chat/share')) return 'shared_chat'
  if (path.startsWith('/embed')) return 'embed'
  if (path.startsWith('/nexus')) return 'nexus'
  if (path.startsWith('/spaces')) return 'space'
  if (path.startsWith('/chat')) {
    return isAnonymous ? 'public_chat' : 'authenticated_chat'
  }

  return 'unknown'
}

export function getActivationChatType(input: {
  isPublic?: boolean
  isGroupChat?: boolean | null
}): ActivationChatType {
  if (input.isPublic) return 'public_session'
  if (input.isGroupChat) return 'group'

  return 'personal'
}

export function getActivationErrorType(error: unknown): ActivationErrorType {
  if (!(error instanceof Error)) return 'unknown'

  const message = error.message.toLowerCase()
  const name = error.name.toLowerCase()

  if (name.includes('abort') || message.includes('abort')) return 'abort'
  if (message.includes('rate limit') || message.includes('too many requests')) {
    return 'rate_limit'
  }
  if (message.includes('subscription') || message.includes('upgrade')) {
    return 'subscription_required'
  }
  if (message.includes('valid') || message.includes('not found')) return 'validation'
  if (message.includes('network') || message.includes('fetch')) return 'network'

  return 'unknown'
}

export function getActivationReferrerType(
  referrer: string | null | undefined,
  currentOrigin: string | null | undefined
): ActivationReferrerType {
  if (!referrer) return 'direct'

  try {
    const referrerUrl = new URL(referrer)
    const origin = currentOrigin ? new URL(currentOrigin).origin : ''
    const host = referrerUrl.hostname.toLowerCase()

    if (origin && referrerUrl.origin === origin) return 'internal'
    if (
      host.includes('google.') ||
      host.includes('bing.') ||
      host.includes('duckduckgo.') ||
      host.includes('yahoo.')
    ) {
      return 'search'
    }
    if (
      host.includes('linkedin.') ||
      host.includes('facebook.') ||
      host.includes('reddit.') ||
      host.includes('twitter.') ||
      host.includes('x.com')
    ) {
      return 'social'
    }

    return 'external'
  } catch {
    return 'unknown'
  }
}

export async function hashActivationIdentifier(value: string): Promise<string> {
  const normalized = value.trim()
  if (!normalized) return fallbackHash('empty')

  const subtle = globalThis.crypto?.subtle
  if (!subtle) return fallbackHash(normalized)

  const digest = await subtle.digest(
    'SHA-256',
    new TextEncoder().encode(normalized)
  )
  const bytes = Array.from(new Uint8Array(digest))
  const hash = bytes.map((byte) => byte.toString(16).padStart(2, '0')).join('')

  return `sha256_${hash}`
}

export function getPublicSessionActivationState(): PublicSessionActivationState {
  if (typeof window === 'undefined') {
    return {
      had_first_ai_response: false,
      had_second_message: false
    }
  }

  try {
    const sessionChatId = window.sessionStorage.getItem('public_chat_session_id')
    if (!sessionChatId) {
      return {
        had_first_ai_response: false,
        had_second_message: false
      }
    }

    const rawMessages = window.sessionStorage.getItem(`public_chat_${sessionChatId}`)
    if (!rawMessages) {
      return {
        had_first_ai_response: false,
        had_second_message: false
      }
    }

    const parsed = JSON.parse(rawMessages) as unknown
    if (!Array.isArray(parsed)) {
      return {
        had_first_ai_response: false,
        had_second_message: false
      }
    }

    let userMessageCount = 0
    let assistantMessageCount = 0

    for (const message of parsed) {
      const role = readMessageRole(message)
      if (role === 'user') userMessageCount += 1
      if (role === 'assistant') assistantMessageCount += 1
    }

    return {
      had_first_ai_response: assistantMessageCount > 0,
      had_second_message: userMessageCount >= 2
    }
  } catch {
    return {
      had_first_ai_response: false,
      had_second_message: false
    }
  }
}

export function getPublicSessionActivationSurface(): ActivationSurface {
  if (typeof window === 'undefined') return 'public_chat'

  try {
    const sessionChatId = window.sessionStorage.getItem('public_chat_session_id')
    return sessionChatId?.startsWith('shared_') ? 'shared_chat' : 'public_chat'
  } catch {
    return 'public_chat'
  }
}

export function trackActivationEvent<Name extends ActivationEventName>(
  eventName: Name,
  properties: ActivationEventPropertiesByName[Name]
) {
  const event = buildActivationEvent(eventName, properties)
  return track(event.event, event.properties)
}

export function trackPublicChatStarterPromptClicked(
  properties: WithOptionalSessionId<PublicChatStarterPromptClickedProperties>
) {
  return trackActivationEvent(
    ActivationEventNames.PUBLIC_CHAT_STARTER_PROMPT_CLICKED,
    withSessionId<PublicChatStarterPromptClickedProperties>(properties)
  )
}

export function trackChatFirstUserMessageSubmitted(
  properties: WithOptionalSessionId<ChatFirstUserMessageSubmittedProperties>
) {
  return trackActivationEvent(
    ActivationEventNames.CHAT_FIRST_USER_MESSAGE_SUBMITTED,
    withSessionId<ChatFirstUserMessageSubmittedProperties>(properties)
  )
}

export function trackChatFirstAiResponseStarted(
  properties: WithOptionalSessionId<ChatFirstAiResponseStartedProperties>
) {
  return trackActivationEvent(
    ActivationEventNames.CHAT_FIRST_AI_RESPONSE_STARTED,
    withSessionId<ChatFirstAiResponseStartedProperties>(properties)
  )
}

export function trackChatFirstAiResponseSucceeded(
  properties: WithOptionalSessionId<ChatFirstAiResponseSucceededProperties>
) {
  return trackActivationEvent(
    ActivationEventNames.CHAT_FIRST_AI_RESPONSE_SUCCEEDED,
    withSessionId<ChatFirstAiResponseSucceededProperties>(properties)
  )
}

export function trackChatFirstAiResponseFailed(
  properties: WithOptionalSessionId<ChatFirstAiResponseFailedProperties>
) {
  return trackActivationEvent(
    ActivationEventNames.CHAT_FIRST_AI_RESPONSE_FAILED,
    withSessionId<ChatFirstAiResponseFailedProperties>(properties)
  )
}

export function trackChatSecondUserMessageSubmitted(
  properties: WithOptionalSessionId<ChatSecondUserMessageSubmittedProperties>
) {
  return trackActivationEvent(
    ActivationEventNames.CHAT_SECOND_USER_MESSAGE_SUBMITTED,
    withSessionId<ChatSecondUserMessageSubmittedProperties>(properties)
  )
}

export function trackFirstChatMessageSent(
  properties: FirstChatMessageSentProperties
) {
  return track(EventNames.FIRST_CHAT_MESSAGE_SENT, properties)
}

export function trackChatMessageSent(properties: ChatMessageSentProperties) {
  return track(EventNames.CHAT_MESSAGE_SENT, properties)
}

export function trackHomepageChatSubmitted(
  properties: HomepageChatSubmittedProperties
) {
  return track(EventNames.HOMEPAGE_CHAT_SUBMITTED, properties)
}

export function trackHomepageChatResponseShown(
  properties: HomepageChatResponseShownProperties
) {
  return track(EventNames.HOMEPAGE_CHAT_RESPONSE_SHOWN, properties)
}

export function trackHomepageCtaClicked(properties: HomepageCtaClickedProperties) {
  return track(EventNames.HOMEPAGE_CTA_CLICKED, properties)
}

export function trackSaveChatCtaViewed(
  properties: ActivationEventPropertiesByName[typeof ActivationEventNames.SAVE_CHAT_CTA_VIEWED]
) {
  return trackActivationEvent(ActivationEventNames.SAVE_CHAT_CTA_VIEWED, properties)
}

export function trackSaveChatCtaClicked(
  properties: ActivationEventPropertiesByName[typeof ActivationEventNames.SAVE_CHAT_CTA_CLICKED]
) {
  return trackActivationEvent(ActivationEventNames.SAVE_CHAT_CTA_CLICKED, properties)
}

export function trackSignupCompletedFromActivation(
  properties: ActivationEventPropertiesByName[
    typeof ActivationEventNames.SIGNUP_COMPLETED_FROM_ACTIVATION
  ]
) {
  return trackActivationEvent(
    ActivationEventNames.SIGNUP_COMPLETED_FROM_ACTIVATION,
    properties
  )
}

export function trackChatShareClicked(
  properties: ActivationEventPropertiesByName[typeof ActivationEventNames.CHAT_SHARE_CLICKED]
) {
  return trackActivationEvent(ActivationEventNames.CHAT_SHARE_CLICKED, properties)
}

export function trackSharedChatViewed(
  properties: ActivationEventPropertiesByName[typeof ActivationEventNames.SHARED_CHAT_VIEWED]
) {
  return trackActivationEvent(ActivationEventNames.SHARED_CHAT_VIEWED, properties)
}

export function trackSharedChatFollowupSubmitted(
  properties: SharedChatFollowupSubmittedProperties
) {
  return trackActivationEvent(
    ActivationEventNames.SHARED_CHAT_FOLLOWUP_SUBMITTED,
    properties
  )
}

export function getModelResponseFailureStage(
  completedModelRun: boolean
): ActivationFailureStage {
  return completedModelRun ? 'message_persist' : 'model_response'
}
